import { useAdmin } from '@vtex/raccoon-next'
import { PromotionSupportStatus, useFeature } from 'hooks'
import { useRouter } from 'next/router'
import { useCallback, useEffect, useState } from 'react'
import { languageIsSupported } from 'shared'
import { PROMOTION_TYPE } from 'typings'
import { buildSearchParams } from 'ui'
import { Loading } from './Form/Loading'

const SUPPORTED_PROMOTION_TYPES =
  process.env.NEXT_PUBLIC_SUPPORTED_PROMOTION_TYPES ?? ''

const MASTER_WORKSPACE = 'master' as const

export const NEW_PROMOTION_FORM = 'newPromotionForm' as const
export const PROMOTION_FORM = 'promotionForm' as const
export const ARCHIVED_PROMOTION_FORM = 'archivedPromotionForm' as const

type redirectType =
  | typeof NEW_PROMOTION_FORM
  | typeof PROMOTION_FORM
  | typeof ARCHIVED_PROMOTION_FORM

type LegacyRedirectWrapperProps = {
  to: redirectType
  type: string | null
  promoId?: string
  duplicate?: string | null
  children: () => React.ReactNode
}

export function LegacyRedirectWrapper(props: LegacyRedirectWrapperProps) {
  const { to, type, promoId, children, duplicate } = props

  const { account, workspace, locale } = useAdmin()

  const [isPendingType, setIsPendingType] = useState(true)

  const statusFallback =
    type && SUPPORTED_PROMOTION_TYPES.split(',').includes(type)
      ? PromotionSupportStatus.enabled
      : PromotionSupportStatus.disabled

  const { getFeature } = useFeature()

  const promotionSupportStatus =
    getFeature(type as PROMOTION_TYPE) ?? statusFallback

  const typeIsSupported = useCallback(
    () =>
      Boolean(
        type && promotionSupportStatus !== PromotionSupportStatus.disabled
      ),
    [promotionSupportStatus, type]
  )

  const router = useRouter()

  const redirect = useCallback(() => {
    const searchParams = buildSearchParams(router.query)

    const baseUrl =
      MASTER_WORKSPACE === workspace
        ? `https://${account}.myvtex.com`
        : `https://${workspace}--${account}.myvtex.com`
    const PATHS = {
      [NEW_PROMOTION_FORM]: `${baseUrl}/admin/iframe/rnb/#/benefit/new?backUrl=${window.location.origin}&mode=migration&${searchParams}`,
      [PROMOTION_FORM]: `${baseUrl}/admin/iframe/rnb/#/benefit/${promoId}?backUrl=${window.location.origin}&mode=migration&type=${type}`,
      [ARCHIVED_PROMOTION_FORM]: `${baseUrl}/admin/iframe/rnb/#/benefit/${promoId}?backUrl=${window.location.origin}/archived&mode=migration&type=${type}&archived=true`,
    }
    if (duplicate && to !== NEW_PROMOTION_FORM) {
      return window.location.assign(`${PATHS[to]}&duplicate=${duplicate}`)
    }
    window.location.assign(PATHS[to])
  }, [account, duplicate, promoId, router.query, to, type, workspace])

  useEffect(() => {
    if (type !== null) {
      if (to === ARCHIVED_PROMOTION_FORM) {
        redirect()
        return
      }
      if (typeIsSupported() && languageIsSupported(type, locale)) {
        setIsPendingType(false)
      } else {
        redirect()
      }
    }
  }, [locale, redirect, to, type, typeIsSupported])

  if (isPendingType) return <Loading />

  return <>{children()}</>
}
